import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Wrench, User, Building, MapPin, Phone, CalendarDays, Package, Pencil } from 'lucide-react';

const PRIORITY_CONFIG = {
  critical: { label: 'Critic', color: 'bg-red-600 text-white' },
  urgent: { label: 'Urgent', color: 'bg-orange-500 text-white' },
  high: { label: 'Ridicat', color: 'bg-amber-500 text-white' },
  medium: { label: 'Mediu', color: 'bg-yellow-400 text-slate-900' },
  low: { label: 'Scazut', color: 'bg-green-500 text-white' }
};

const TicketInfoSidebar = ({ ticket, onEditPartsEta, canEdit }) => {
  // Compute remaining days for parts ETA
  const partsEtaInfo = (() => {
    if (ticket.status !== 'waiting_parts' || !ticket.parts_eta_days) return null;
    const etaDate = ticket.parts_eta_date ? new Date(ticket.parts_eta_date) : null;
    let remainingDays = null;
    if (etaDate) {
      const now = new Date();
      const diffMs = etaDate - now;
      remainingDays = Math.ceil(diffMs / 86400000);
    }
    return {
      days: ticket.parts_eta_days,
      date: etaDate,
      remaining: remainingDays,
      setAt: ticket.parts_eta_set_at ? new Date(ticket.parts_eta_set_at) : null,
      setBy: ticket.parts_eta_set_by
    };
  })();

  return (
    <>
      {/* Parts ETA Card - shown only when waiting for parts */}
      {partsEtaInfo && (
        <Card className="border-orange-200 bg-orange-50/40" data-testid="parts-eta-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-600" />
              <span className="text-orange-800">Termen Piese</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-3xl font-bold text-orange-700" data-testid="parts-eta-days-display">{partsEtaInfo.days}<span className="text-base font-medium text-orange-600 ml-1">zile</span></p>
                {partsEtaInfo.date && (
                  <p className="text-sm text-slate-700 mt-1">
                    Estimat: <span className="font-semibold">{partsEtaInfo.date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  </p>
                )}
                {partsEtaInfo.remaining !== null && (
                  <p className={`text-xs mt-1 font-medium ${partsEtaInfo.remaining < 0 ? 'text-red-600' : partsEtaInfo.remaining <= 3 ? 'text-amber-600' : 'text-slate-500'}`}>
                    {partsEtaInfo.remaining < 0
                      ? `Depasit cu ${Math.abs(partsEtaInfo.remaining)} zile`
                      : partsEtaInfo.remaining === 0
                      ? 'Ar trebui sa ajunga astazi'
                      : `Mai sunt ${partsEtaInfo.remaining} zile`}
                  </p>
                )}
              </div>
              {canEdit && onEditPartsEta && (
                <Button variant="outline" size="sm" className="h-8 text-orange-700 border-orange-300 hover:bg-orange-100" onClick={onEditPartsEta} data-testid="edit-parts-eta-btn">
                  <Pencil className="w-3 h-3 mr-1" /> Modifica
                </Button>
              )}
            </div>
            {partsEtaInfo.setBy && (
              <div className="pt-2 border-t border-orange-200 text-xs text-slate-500">
                Setat de <span className="font-medium text-slate-700">{partsEtaInfo.setBy}</span>
                {partsEtaInfo.setAt && <> pe {partsEtaInfo.setAt.toLocaleDateString('ro-RO')}</>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Equipment Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="w-4 h-4 text-slate-500" /> Echipament
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Nume</p>
            <p className="font-medium text-slate-900">{ticket.equipment_name || 'N/A'}</p>
          </div>
          {ticket.equipment_code && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Cod</p>
              <p className="font-mono text-sm text-slate-700">{ticket.equipment_code}</p>
            </div>
          )}
          {ticket.reporter_name && (
            <>
              <Separator />
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-slate-500">Raportat de</p>
                  <p className="text-sm font-bold text-slate-900" data-testid="ticket-reporter-name">{ticket.reporter_name}</p>
                </div>
              </div>
              {ticket.reporter_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-500" />
                  <div>
                    <p className="text-xs text-slate-500">Telefon</p>
                    <a href={`tel:${ticket.reporter_phone}`} className="text-sm font-medium text-blue-600 hover:underline" data-testid="ticket-reporter-phone">{ticket.reporter_phone}</a>
                  </div>
                </div>
              )}
              {ticket.reporter_department && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-purple-500" />
                  <div>
                    <p className="text-xs text-slate-500">Departament</p>
                    <p className="text-sm font-medium text-slate-700" data-testid="ticket-reporter-dept">{ticket.reporter_department}</p>
                  </div>
                </div>
              )}
            </>
          )}
          <Separator />
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Sediu</p>
              <p className="text-sm font-medium">{ticket.plant_name || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Locatie</p>
              <p className="text-sm font-medium">{ticket.location_name || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assignment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4 text-slate-500" /> Asignare
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {ticket.assigned_to_names?.length > 0 ? (
            <div className="space-y-2">
              {ticket.assigned_to_names.map((name, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#0052CC]/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#0052CC]" />
                  </div>
                  <span className="text-sm font-medium">{name}</span>
                </div>
              ))}
            </div>
          ) : ticket.accepted_by_name ? (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <User className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <span className="text-sm font-medium">{ticket.accepted_by_name}</span>
                <p className="text-xs text-slate-400">Acceptat de</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Neasignat</p>
          )}
          {ticket.priority_assigned && (
            <div className="mt-2">
              <p className="text-xs text-slate-500">Prioritate asignata</p>
              <Badge className={`mt-1 ${PRIORITY_CONFIG[ticket.priority_assigned]?.color || ''}`}>
                {PRIORITY_CONFIG[ticket.priority_assigned]?.label || ticket.priority_assigned}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-500" /> Cronologie
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Creat</span>
            <span className="font-medium">{new Date(ticket.created_at).toLocaleString('ro-RO')}</span>
          </div>
          {ticket.updated_at && (
            <div className="flex justify-between">
              <span className="text-slate-500">Actualizat</span>
              <span className="font-medium">{new Date(ticket.updated_at).toLocaleString('ro-RO')}</span>
            </div>
          )}
          {ticket.closed_at && (
            <div className="flex justify-between">
              <span className="text-slate-500">Inchis</span>
              <span className="font-medium">{new Date(ticket.closed_at).toLocaleString('ro-RO')}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default TicketInfoSidebar;
